<script setup lang="ts">
  import {
    CustomersService,
    ICreateCustomerDto,
    IUpdateCustomerDto,
  } from '@/api/services/customers.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import ModalChooseAddress from '@/components/modals/ModalChooseAddress.vue';
  import { IDeliveryInfo } from '@/interfaces/IDeliveryInfo';

  const props = defineProps<{
    customerToEdit?: ICustomer;
  }>();

  const customerData = ref<ICreateCustomerDto | IUpdateCustomerDto>({
    restaurantId: 0,
    name: '',
    customId: '',
    taxIdType: '',
    taxId: '',
    address: '',
    address2: '',
    addressComment: '',
    lat: undefined,
    lng: undefined,
    tel: '',
    email: '',
  });

  // Phone input handling
  const rawPhone = ref('');
  function phoneChanged(
    number: string,
    data: { number: { e164: string }; valid: boolean }
  ) {
    customerData.value.tel = data.valid ? data.number.e164 : null;
  }

  onMounted(() => {
    if (props.customerToEdit) {
      customerData.value = <IUpdateCustomerDto>{
        name: props.customerToEdit.name,
        customId: props.customerToEdit.customId,
        taxIdType: props.customerToEdit.taxIdType,
        taxId: props.customerToEdit.taxId,
        address: props.customerToEdit.address ?? null,
        address2: props.customerToEdit.address2 ?? null,
        addressComment: props.customerToEdit.addressComment ?? null,
        lat: props.customerToEdit.lat ?? null,
        lng: props.customerToEdit.lng ?? null,
        tel: props.customerToEdit.tel ?? null,
        email: props.customerToEdit.email ?? null,
      };
      rawPhone.value = props.customerToEdit.tel || '';
    }
  });

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const valid = ref(false);
  const modals = useModal();
  const { proxy } = getCurrentInstance();

  const saving = ref(false);
  async function save() {
    if (!valid.value || saving.value) return;

    try {
      saving.value = true;

      let customer: ICustomer;
      if (!props.customerToEdit) {
        (customerData.value as ICreateCustomerDto).restaurantId =
          restaurantStore.restaurant.id;
        customer = await CustomersService.create(
          customerData.value as ICreateCustomerDto
        );
      } else {
        customer = await CustomersService.update(
          props.customerToEdit.id,
          customerData.value
        );
      }
      saving.value = false;
      proxy.$parent.$emit('saved', customer);
      close();
    } catch (error) {
      ui.serverError(error, 'Error al guardar el cliente');
      saving.value = false;
    }
  }

  const close = () => {
    modals.hide('customer');
  };

  function openAddressModal() {
    modals.show(
      // @ts-ignore reuse existing modal
      ModalChooseAddress,
      {
        deliveryInfo: customerData.value.address
          ? {
              address: customerData.value.address,
              address2: customerData.value.address2 ?? null,
              addressComment: customerData.value.addressComment ?? null,
              lat: customerData.value.lat ?? null,
              lng: customerData.value.lng ?? null,
            }
          : undefined,
      },
      {
        name: 'choose-address',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        save: (place: IDeliveryInfo) => {
          customerData.value.address = place.address;
          customerData.value.address2 = place.address2;
          customerData.value.addressComment = place.addressComment;
          customerData.value.lat = place.lat;
          customerData.value.lng = place.lng;
          modals.hide('choose-address');
        },
      }
    );
  }

  function clearAddress() {
    customerData.value.address = null;
    customerData.value.address2 = null;
    customerData.value.addressComment = null;
    customerData.value.lat = null;
    customerData.value.lng = null;
  }

  const fullAddress = computed(() => {
    if (!customerData.value.address) return null;

    let address = customerData.value.address;

    if (customerData.value.address2) {
      address += ', ' + customerData.value.address2;
    }

    if (customerData.value.addressComment) {
      address += ` (${customerData.value.addressComment})`;
    }

    return address;
  });

  // Only show IVA related fields when restaurant country is Argentina
  const showIvaFields = computed(
    () => restaurantStore.restaurant?.country === 'AR'
  );
</script>

<template>
  <v-card class="modal-customer">
    <v-card-title class="modal-customer-title">
      <span>{{ customerToEdit ? 'Editar cliente' : 'Nuevo cliente' }}</span>
    </v-card-title>
    <v-card-text class="modal-customer-text">
      <div class="modal-body">
        <v-form v-model="valid" @submit.prevent>
          <v-text-field
            v-model="customerData.name"
            label="Nombre"
            :rules="$rule('required|max255')"
            outlined
            dense
            class="form-field"
          />

          <!-- <v-text-field
            v-model="customerData.customId"
            label="Identificador"
            hint="Identificador único para el cliente (por ejemplo, teléfono, DNI, etc.)"
            persistent-hint
            :rules="$rule('max255')"
            outlined
            dense
            class="form-field"
          /> -->

          <vue-tel-input-vuetify
            v-model="rawPhone"
            label="Teléfono"
            class="form-field"
            dense
            @input="phoneChanged"
            persistent-hint
            :default-country="restaurantStore.restaurant?.country"
          />

          <v-text-field
            v-model="customerData.email"
            label="E-mail"
            :rules="$rule('email|max128')"
            outlined
            dense
            class="form-field"
          />

          <!-- Tax ID (IVA) -->
          <v-row class="tax-id-row" v-if="showIvaFields">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="customerData.taxId"
                label="Nº IVA / CUIT"
                outlined
                dense
                hide-details
                class="form-field"
              />
            </v-col>
          </v-row>

          <!-- Address selector -->
          <div class="address-selector-container">
            <div class="address-display">
              <v-icon small class="address-selector-icon"
                >mdi-map-marker</v-icon
              >
              <span v-if="customerData.address" class="address-selector-text">
                {{ fullAddress }}
              </span>
              <span v-else class="address-selector-placeholder">
                No se ha especificado una dirección.
              </span>
            </div>
            <v-btn
              small
              outlined
              color="primary"
              @click="openAddressModal"
              class="address-selector-button"
            >
              <v-icon left small>mdi-map-search</v-icon>
              <span>{{
                customerData.address ? 'Editar dirección' : 'Elegir dirección'
              }}</span>
            </v-btn>
            <v-btn
              v-if="customerData.address"
              class="tw-ml-2"
              small
              icon
              color="secondary"
              @click="clearAddress"
              ><v-icon small>mdi-delete-outline</v-icon></v-btn
            >
          </div>
        </v-form>
      </div>
    </v-card-text>
    <v-card-actions class="modal-customer-actions">
      <v-spacer />
      <v-btn text @click="close" :disabled="saving">Cancelar</v-btn>
      <v-btn color="primary" @click="save" :disabled="!valid" :loading="saving">
        Guardar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-customer {
    .modal-customer-title {
      // Potentially add some styles for the title, like padding or border
    }
    .modal-customer-text {
      @apply tw-p-0; // Remove padding if modal-body handles it, or adjust as needed
    }
    .modal-body {
      @apply tw-p-6; // Increased padding for better spacing

      .form-field {
        @apply tw-mb-1; // Add margin-bottom to space out fields
      }

      .tax-id-row {
      }

      .address-selector-container {
        @apply tw-mt-4 tw-p-3 tw-border tw-border-gray-200 tw-rounded-md;

        .address-display {
          @apply tw-flex tw-items-center tw-mb-2;

          .address-selector-icon {
            @apply tw-mr-2 tw-text-gray-500;
          }

          .address-selector-text {
            @apply tw-text-sm tw-text-gray-700 tw-truncate;
          }

          .address-selector-placeholder {
            @apply tw-text-sm tw-text-gray-500 tw-italic;
          }
        }

        .address-selector-button {
          @apply tw-rounded-lg tw-w-full md:tw-w-auto;
        }
      }
    }

    .modal-customer-actions {
      @apply tw-px-6 tw-pb-4; // Add padding to actions
    }
  }
</style>
